import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import GlobalStyles from '@mui/material/GlobalStyles';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import { useDateFormat } from '@/hooks/useDateFormat';
import type { LoadingTaskDto } from '@/types/outbound';

interface LoadingSlipDialogProps {
  task: LoadingTaskDto | null;
  onClose: () => void;
}

/** Printable Dispatch Order Slip for a Loaded LoadingTask — a plain, fixed-layout A4 document (not
 * a Label Designer template), listing whichever items the load actually carries: Packages when
 * Packing ran, DispatchMaterialIssueLines otherwise (see LoadingTaskDto's own doc comment). */
export default function LoadingSlipDialog({ task, onClose }: LoadingSlipDialogProps) {
  const { t } = useTranslation('loading');
  const { formatDateTime } = useDateFormat();

  if (!task) return null;

  const isPackageBased = task.packages.length > 0;

  return (
    <>
      <GlobalStyles
        styles={{
          '@media print': {
            'body *': { visibility: 'hidden' },
            '#loading-slip-print-area, #loading-slip-print-area *': { visibility: 'visible' },
            '#loading-slip-print-area': { position: 'absolute', top: 0, left: 0, width: '100%' },
          },
        }}
      />
      <Dialog open={Boolean(task)} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{t('slip.title')}</DialogTitle>
        <DialogContent>
          <Box id="loading-slip-print-area">
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="h5">{t('slip.title')}</Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {task.loadingTaskNumber}
                </Typography>
              </Box>

              <GridContainer columns={12} spacing={2}>
                <GridItem xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                    {t('slip.loadedDate')}
                  </Typography>
                  <Typography variant="body2">{task.loadedDate ? formatDateTime(task.loadedDate) : '—'}</Typography>
                </GridItem>
                <GridItem xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                    {t('form.carrier')}
                  </Typography>
                  <Typography variant="body2">{task.carrierName ?? '—'}</Typography>
                </GridItem>
                <GridItem xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                    {t('form.vehicleNo')}
                  </Typography>
                  <Typography variant="body2">{task.vehicleNo ?? '—'}</Typography>
                </GridItem>
                <GridItem xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                    {t('form.invoiceNumber')}
                  </Typography>
                  <Typography variant="body2">{task.invoiceNumber ?? '—'}</Typography>
                </GridItem>
                <GridItem xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                    {t('form.ewayBillNo')}
                  </Typography>
                  <Typography variant="body2">{task.ewayBillNo ?? '—'}</Typography>
                </GridItem>
              </GridContainer>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle2">{t('slip.dispatchOrders')}</Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('slip.orderNo')}</TableCell>
                      <TableCell>{t('slip.customer')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {task.dispatchOrders.map((o) => (
                      <TableRow key={o.id}>
                        <TableCell>{o.orderNumber}</TableCell>
                        <TableCell>{o.customerName}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  </Table>
                </Box>
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle2">{t('slip.items')}</Typography>
                {isPackageBased ? (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('form.packageNo')}</TableCell>
                        <TableCell>{t('form.materialCode')}</TableCell>
                        <TableCell>{t('form.materialName')}</TableCell>
                        <TableCell align="right">{t('form.qty')}</TableCell>
                        <TableCell align="right">{t('form.weight')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {task.packages.map((pkg) => (
                        <TableRow key={pkg.packageId}>
                          <TableCell>{pkg.packageNo}</TableCell>
                          <TableCell>{pkg.materialCode}</TableCell>
                          <TableCell>{pkg.materialName}</TableCell>
                          <TableCell align="right">{pkg.qty}</TableCell>
                          <TableCell align="right">{pkg.weight ?? '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    </Table>
                  </Box>
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('form.materialCode')}</TableCell>
                        <TableCell>{t('form.materialName')}</TableCell>
                        <TableCell>{t('form.barcode')}</TableCell>
                        <TableCell align="right">{t('form.qty')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {task.materialIssueLines.map((line) => (
                        <TableRow key={line.lineId}>
                          <TableCell>{line.materialCode}</TableCell>
                          <TableCell>{line.materialName}</TableCell>
                          <TableCell>{line.barcode ?? '—'}</TableCell>
                          <TableCell align="right">{line.qty}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    </Table>
                  </Box>
                )}
              </Stack>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose}>{t('cancel')}</Button>
          <Button variant="contained" onClick={() => window.print()}>
            {t('slip.print')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
